import React, { useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
  Box, Typography, Button, IconButton, TextField, Menu, MenuItem,
  ListItemIcon, ListItemText,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import SearchIcon from '@material-ui/icons/Search';
import TimelineIcon from '@material-ui/icons/Timeline';
import PlaceIcon from '@material-ui/icons/Place';
import CropSquareIcon from '@material-ui/icons/CropSquare';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import ImportIcon from '@material-ui/icons/CallSplit';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import {
  AREA_CATEGORIES, AREA_TYPE_COLORS, AREA_TYPE_LABELS,
  getAreaTypeColor, getAreaTypeLabel, resolveAreaType,
} from '../../../SiteTakeoff/siteTakeoffConstants';

const useStyles = makeStyles(() => ({
  actionRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 16,
  },
  actionBtn: {
    flex: 1,
    textTransform: 'none',
    fontSize: 13,
    fontWeight: 500,
  },
  searchBox: {
    marginBottom: 12,
    '& .MuiOutlinedInput-root': { fontSize: 13 },
    '& .MuiOutlinedInput-input': { padding: '8px 8px' },
  },
  legacyBanner: {
    background: '#fff8e1',
    border: '1px solid #ffe082',
    borderLeft: '4px solid #ff9800',
    borderRadius: 6,
    padding: 12,
    marginBottom: 16,
  },
  legacyTitle: {
    fontSize: 12,
    fontWeight: 600,
    color: '#e65100',
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    marginBottom: 4,
  },
  legacyHint: {
    fontSize: 11,
    color: '#b45309',
    marginBottom: 10,
    lineHeight: 1.4,
  },
  legacyBtn: {
    textTransform: 'none',
    fontSize: 12,
    fontWeight: 500,
  },
  categorySection: {
    marginBottom: 20,
  },
  categoryHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 10,
  },
  categorySwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
    flexShrink: 0,
  },
  categoryLabel: {
    fontSize: 12,
    fontWeight: 500,
    color: '#666',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  categoryMeta: {
    fontSize: 11,
    color: '#999',
    marginLeft: 4,
  },
  areaCard: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    padding: '12px 14px',
    background: '#ffffff',
    border: '1px solid #e0e0e0',
    borderRadius: 8,
    cursor: 'pointer',
    marginBottom: 8,
    transition: 'all 0.15s ease',
    '&:hover': {
      borderColor: '#bdbdbd',
      background: '#fafafa',
    },
  },
  areaDot: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0,
  },
  areaInfo: {
    flex: 1,
    minWidth: 0,
  },
  areaName: {
    fontSize: 14,
    fontWeight: 500,
    color: '#333',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  areaMeta: {
    fontSize: 12,
    color: '#999',
    marginTop: 2,
  },
  totalsFooter: {
    padding: 14,
    background: '#f5f5f5',
    borderRadius: 8,
    marginTop: 8,
  },
  totalsRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  totalsLabel: {
    fontSize: 13,
    color: '#666',
  },
  totalsValue: {
    fontSize: 16,
    fontWeight: 500,
    color: '#333',
  },
  totalsMeta: {
    fontSize: 11,
    color: '#999',
    marginTop: 4,
  },
  empty: {
    padding: 32,
    textAlign: 'center',
    color: '#999',
    fontSize: 13,
  },
}));

const formatSF = (num) => {
  const n = parseFloat(num) || 0;
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

// Derived category for the V2 grouped view. Takes each area and picks the
// display category (Turf / Desert+Gravel / Beds / Hardscape / ...)
const getDisplayCategory = (areaType) => {
  const resolved = resolveAreaType(areaType);
  for (const [catKey, cat] of Object.entries(AREA_CATEGORIES)) {
    if (cat.types.includes(resolved)) return { key: catKey, label: cat.label };
  }
  return { key: 'OTHER', label: 'Other' };
};

// Pick a representative color for a category based on the first area in it
const getCategoryColor = (areaType) => getAreaTypeColor(areaType);

const AreasMode = ({
  areas = [],
  onDrawArea,
  onEditArea,
  jobsite,
  hasLegacyAreas,
  legacyAreaCount = 0,
  onImportLegacyAreas,
  saving,
}) => {
  const classes = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const [drawMenuAnchor, setDrawMenuAnchor] = useState(null);

  const filtered = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return areas;
    return areas.filter(a => {
      const name = (a.name || '').toLowerCase();
      const type = getAreaTypeLabel(a.areaType).toLowerCase();
      return name.includes(q) || type.includes(q);
    });
  }, [areas, searchQuery]);

  // Group areas by display category
  const grouped = useMemo(() => {
    const groups = {};
    filtered.forEach(area => {
      const { key, label } = getDisplayCategory(area.areaType);
      if (!groups[key]) {
        groups[key] = {
          key,
          label,
          color: getCategoryColor(area.areaType),
          areas: [],
          totalSF: 0,
        };
      }
      groups[key].areas.push(area);
      groups[key].totalSF += parseFloat(area.area) || 0;
    });
    return Object.values(groups);
  }, [filtered]);

  // Totals
  const totals = useMemo(() => {
    let totalSF = 0;
    let totalLF = 0;
    filtered.forEach(a => {
      totalSF += parseFloat(a.area) || 0;
      if (a.geometryType === 'polyline') {
        totalLF += parseFloat(a.length) || 0;
      } else {
        totalLF += parseFloat(a.perimeter) || 0;
      }
    });
    return {
      totalSF,
      totalLF,
      totalAcres: totalSF / 43560,
    };
  }, [filtered]);

  const handleDrawClick = (e) => {
    setDrawMenuAnchor(e.currentTarget);
  };

  const handleDrawTool = (tool) => {
    setDrawMenuAnchor(null);
    if (onDrawArea) onDrawArea(tool);
  };

  return (
    <Box>
      {/* Action row */}
      <div className={classes.actionRow}>
        <Button
          variant="outlined"
          className={classes.actionBtn}
          startIcon={<AddIcon />}
          onClick={handleDrawClick}
          disabled={!jobsite?._id}
          style={{
            background: '#EEF2FF',
            color: '#4F46E5',
            borderColor: '#C7D2FE',
          }}
        >
          Draw area
        </Button>
        <Menu
          anchorEl={drawMenuAnchor}
          open={Boolean(drawMenuAnchor)}
          onClose={() => setDrawMenuAnchor(null)}
        >
          <MenuItem onClick={() => handleDrawTool('polygon')}>
            <ListItemIcon><CropSquareIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Polygon" secondary="Beds, turf, hardscape" />
          </MenuItem>
          <MenuItem onClick={() => handleDrawTool('polyline')}>
            <ListItemIcon><TimelineIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Line / Trail" secondary="Sidewalks, edging, trails" />
          </MenuItem>
          <MenuItem onClick={() => handleDrawTool('ellipse')}>
            <ListItemIcon><RadioButtonUncheckedIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Circle / Ellipse" secondary="Round features" />
          </MenuItem>
          <MenuItem onClick={() => handleDrawTool('point')}>
            <ListItemIcon><PlaceIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary="Point Marker" secondary="Trees, fixtures" />
          </MenuItem>
        </Menu>
      </div>

      {/* Legacy area import banner — only when pre-Takeoff areas exist and no takeoff yet */}
      {hasLegacyAreas && legacyAreaCount > 0 && (
        <div className={classes.legacyBanner}>
          <div className={classes.legacyTitle}>
            <CloudDownloadIcon style={{ fontSize: 16 }} />
            Legacy areas detected
          </div>
          <div className={classes.legacyHint}>
            This jobsite has {legacyAreaCount} {legacyAreaCount === 1 ? 'area' : 'areas'} from
            the old pre-Takeoff system. Import them to create a new takeoff you can edit here.
          </div>
          <Button
            variant="contained"
            size="small"
            fullWidth
            className={classes.legacyBtn}
            startIcon={<ImportIcon style={{ fontSize: 16 }} />}
            onClick={onImportLegacyAreas}
            disabled={saving}
            style={{
              background: '#ff9800',
              color: '#fff',
            }}
          >
            {saving
              ? 'Importing...'
              : `Import ${legacyAreaCount} ${legacyAreaCount === 1 ? 'area' : 'areas'} as takeoff`}
          </Button>
        </div>
      )}

      {/* Search */}
      {areas.length >= 5 && (
        <TextField
          className={classes.searchBox}
          size="small"
          variant="outlined"
          fullWidth
          placeholder="Search areas..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: <SearchIcon style={{ fontSize: 16, color: '#999', marginRight: 4 }} />,
          }}
        />
      )}

      {/* Empty state */}
      {areas.length === 0 && (
        <div className={classes.empty}>
          {jobsite?._id
            ? 'No areas yet. Click "Draw area" to start mapping this property.'
            : 'Select a jobsite to begin.'}
        </div>
      )}

      {/* Grouped area list */}
      {grouped.map(group => (
        <div key={group.key} className={classes.categorySection}>
          <div className={classes.categoryHeader}>
            <div
              className={classes.categorySwatch}
              style={{ background: group.color }}
            />
            <span className={classes.categoryLabel}>{group.label}</span>
            <span className={classes.categoryMeta}>
              {group.areas.length} {group.areas.length === 1 ? 'area' : 'areas'} · {formatSF(group.totalSF)} SF
            </span>
          </div>

          {group.areas.map(area => {
            const areaId = area._id || area.id;
            const color = getAreaTypeColor(area.areaType);
            const typeLabel = getAreaTypeLabel(area.areaType);
            const hasPreEmergent = (area.servicesRequired || []).includes('pre_emergent');
            return (
              <div
                key={areaId}
                className={classes.areaCard}
                onClick={() => onEditArea && onEditArea(areaId)}
              >
                <div className={classes.areaDot} style={{ background: color }} />
                <div className={classes.areaInfo}>
                  <div className={classes.areaName}>
                    {area.name || typeLabel}
                  </div>
                  <div className={classes.areaMeta}>
                    {typeLabel} · {formatSF(area.area)} SF
                    {area.geometryType === 'polyline' && area.length ? ` · ${formatSF(area.length)} LF` : ''}
                  </div>
                </div>
                {hasPreEmergent && (
                  <span style={{
                    fontSize: 10,
                    padding: '2px 6px',
                    background: '#FFFBEB',
                    color: '#92400E',
                    borderRadius: 4,
                  }}>
                    Pre-Em
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Totals footer */}
      {areas.length > 0 && (
        <div className={classes.totalsFooter}>
          <div className={classes.totalsRow}>
            <span className={classes.totalsLabel}>Total landscape</span>
            <span className={classes.totalsValue}>{formatSF(totals.totalSF)} SF</span>
          </div>
          <div className={classes.totalsMeta}>
            {totals.totalAcres.toFixed(2)} acres · {formatSF(totals.totalLF)} LF perimeter
          </div>
        </div>
      )}
    </Box>
  );
};

export default AreasMode;
